/**
 * PersonnelHubPage.js — Unified HR / personnel surface.
 *
 * Phase 1 of the Operations-section personnel consolidation. Collapses
 * three legacy sidebar entries into one tabbed page:
 *
 *   • Personnel        → roster, records, contact info (personnel:read)
 *   • Leave Requests   → PTO / sick / FMLA tracking      (leave:read)
 *   • Personnel Actions → disciplinary, evaluations, PIP (personnel:read)
 *
 * These are sequential phases of the same HR workflow — manage the roster,
 * approve time off, document performance — but living as three separate
 * sidebar items created the same kind of context-switching friction we
 * saw with Telehealth + Telemedicine. One entry with tabs is cleaner.
 *
 * Access Control (Users / Roles / Permissions / Sidebar Visibility) is
 * intentionally kept as a SEPARATE hub. HR data and security policy are
 * different concerns even when administered by overlapping people.
 */
import React, { useState } from 'react';
import { UserCog, CalendarOff, Scale, Lock } from 'lucide-react';
import PersonnelManagementPage from './PersonnelManagementPage';
import LeaveRequestsPage from './LeaveRequestsPage';
import PersonnelActionsPage from './PersonnelActionsPage';

const TABS = [
  { id: 'records', label: 'Roster',           icon: UserCog,     perm: 'personnel:read' },
  { id: 'leave',   label: 'Leave Requests',   icon: CalendarOff, perm: 'leave:read'     },
  { id: 'actions', label: 'Personnel Actions', icon: Scale,       perm: 'personnel:read' },
];

export default function PersonnelHubPage({ user, onNavigate, initialTab }) {
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const visibleTabs = TABS.filter((t) => perms.includes(t.perm));

  const defaultTab = visibleTabs.find((t) => t.id === initialTab)?.id
    || visibleTabs[0]?.id
    || 'records';

  const [activeTab, setActiveTab] = useState(defaultTab);

  if (visibleTabs.length === 0) {
    return (
      <div className="page">
        <h1 className="page-title">Personnel</h1>
        <div className="empty-state" style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
          <Lock size={48} />
          <p>You don't have permission to view personnel surfaces.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page personnel-hub-page">
      <div className="personnel-hub-header">
        <h1 className="page-title">Personnel</h1>
      </div>

      <div className="personnel-hub-tabs" role="tablist" aria-label="Personnel sections">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`personnel-hub-panel-${tab.id}`}
              className={`personnel-hub-tab ${isActive ? 'personnel-hub-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} strokeWidth={isActive ? 2.2 : 1.8} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div
        className="personnel-hub-panel"
        id={`personnel-hub-panel-${activeTab}`}
        role="tabpanel"
      >
        {activeTab === 'records' && (
          <div className="personnel-hub-embedded">
            <PersonnelManagementPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'leave' && (
          <div className="personnel-hub-embedded">
            <LeaveRequestsPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'actions' && (
          <div className="personnel-hub-embedded">
            <PersonnelActionsPage user={user} onNavigate={onNavigate} />
          </div>
        )}
      </div>

      <style jsx>{`
        .personnel-hub-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .personnel-hub-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid #E8ECF1;
          margin-bottom: 16px;
          overflow-x: auto;
        }
        .personnel-hub-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: none;
          background: transparent;
          color: #6C757D;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          white-space: nowrap;
          transition: color 0.15s, border-color 0.15s;
        }
        .personnel-hub-tab:hover { color: #2E8B98; }
        .personnel-hub-tab--active {
          color: #2E8B98;
          border-bottom-color: #2E8B98;
          font-weight: 600;
        }
        .personnel-hub-embedded :global(.page) {
          padding: 0;
          background: transparent;
        }
        .personnel-hub-embedded :global(.page-title) {
          display: none;
        }
      `}</style>
    </div>
  );
}
